import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { AppConstants } from '../commons/app-constants';

@Injectable({ providedIn: 'root' })
export class UserTimezoneService {
  private readonly http = inject(HttpClient);
  private readonly storageKey = 'tms-user-timezone';
  private readonly defaultTz = 'Asia/Kolkata';

  private tz$ = new BehaviorSubject<string>(
    this.validOrDefault(localStorage.getItem(this.storageKey))
  );

  /** 2026-10-06 — a time zone name the browser does not know (saved by an older version) makes every date pipe throw
   *  "Invalid time zone". Fall back to the default instead. */
  private validOrDefault(tz: string | null | undefined): string {
    if (!tz) return this.defaultTz;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
      return tz;
    } catch {
      return this.defaultTz;
    }
  }

  get timezone(): string {
    return this.tz$.value;
  }

  /** 2026-09-15 — true once a logged-in user's profile timezone is known (stored at login/bootstrap).
   *  False on public pages (share links) — those keep the viewer's browser timezone. */
  get hasProfileTimezone(): boolean {
    try { return !!localStorage.getItem(this.storageKey); } catch { return false; }
  }

  load(): void {
    this.http
      .get<{ timezone: string }>(`${AppConstants.API_URL}api/v1/profile/timezone`, {
        withCredentials: true,
      })
      .subscribe({
        next: (res) => {
          const tz = this.validOrDefault(res?.timezone);
          localStorage.setItem(this.storageKey, tz);
          this.tz$.next(tz);
        },
        error: () => {},
      });
  }

  set(tz: string): void {
    tz = this.validOrDefault(tz);
    localStorage.setItem(this.storageKey, tz);
    this.tz$.next(tz);
  }

  clear(): void {
    localStorage.removeItem(this.storageKey);
    this.tz$.next(this.defaultTz);
  }
}
